import { LOCALIZATION_MODES, localiazationModeMapping } from '../../Service'
import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import RadioButton from 'components/RadioButton/RadioButton'
import RobotOnMapEditable from 'components/RobotOnMapEditable/RobotOnMapEditable'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { Point } from 'geojson'
import FacilityDialogGeoOverlay from 'pages/Assets/shared/FacilityDialogGeoOverlay'
import {
  applyAssetDialogMapCamera,
  AssetDialogCameraCancel,
} from 'pages/Assets/shared/assetDialogMapCamera'
import { useFacilityDialogGeoContext } from 'pages/Assets/shared/useFacilityDialogGeoContext'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobotTelemetry } from 'store/selectors/robot'
import { IMapboxElement, IRobotInfo } from 'utils/interfaces'
import { getRobotTypeDisplayName } from 'utils/robotTypeDisplayName'

import { hasValidNonZeroGeoPosition } from './mapRecordingGeo'
import { LOCATION_NAME_SANITIZE_REGEX, MappingFormData, MappingSubMode } from './types'

import style from '../MapRecordingModal.module.css'

const convertYawUiToUserDegrees = (uiYaw: number): number => {
  let userYaw = 90 - uiYaw
  while (userYaw > 180) userYaw -= 360
  while (userYaw <= -180) userYaw += 360
  return userYaw
}

interface MapRecordingViewProps {
  robotInfo: IRobotInfo
  formData: MappingFormData
  onFormDataChange: (data: MappingFormData) => void
  onValidationChange: (isValid: boolean) => void
  isModalOpen: boolean
  isDisabled?: boolean
  allowForceBaseRecord?: boolean
}

const MapRecordingView: React.FC<MapRecordingViewProps> = ({
  robotInfo,
  formData,
  onFormDataChange,
  onValidationChange,
  isModalOpen,
  isDisabled = false,
  allowForceBaseRecord = false,
}) => {
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const currentMapName = telemetry?.localization_info?.current_lidar_map
  const mapRef = useRef<IMapboxElement>(null)
  const [mapInited, setMapInited] = useState(false)
  const [robotPosition, setRobotPosition] = useState<Point | undefined>(undefined)
  const [robotYaw, setRobotYaw] = useState<number | undefined>(undefined)
  const previousSubModeRef = useRef<MappingSubMode>(formData.subMode)
  const [showNewFacilityWarning, setShowNewFacilityWarning] = useState(false)
  const [showForceBaseWarning, setShowForceBaseWarning] = useState(false)
  const [serverMaps, setServerMaps] = useState<string[]>([])
  const [hasBaseMap, setHasBaseMap] = useState(false)
  const cancelFacilityCameraRef = useRef<AssetDialogCameraCancel | null>(null)
  const facilityCameraAppliedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (isModalOpen && robotInfo.facilityId && robotInfo.id) {
      MainApi.fetchRobotMaps(undefined, 1000, undefined, robotInfo.facilityId, robotInfo.id)
        .then(res => {
          if (Array.isArray(res.results)) {
            const names = res.results.map(m => m.name)
            setServerMaps(names)
            setHasBaseMap(res.results.some(m => m.is_base_map))
          }
        })
        .catch(console.error)
    }
  }, [isModalOpen, robotInfo.facilityId, robotInfo.id])

  const availableLocations = useMemo(() => {
    const fromTelemetry = telemetry?.localization_info?.lidar_maps || []
    return Array.from(new Set([...fromTelemetry, ...serverMaps]))
  }, [telemetry?.localization_info?.lidar_maps, serverMaps])

  const rawMode = telemetry?.localization_info?.localization_mode
  const localizationMode =
    (rawMode != null ? localiazationModeMapping[rawMode] : undefined) ?? LOCALIZATION_MODES.UNDEFINED

  const isGNSSMode = localizationMode === LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION
  const isLidarMode = localizationMode === LOCALIZATION_MODES.LIDAR_LOCALIZATION
  const showFacilityOriginMap =
    (formData.subMode === 'new_facility' || formData.subMode === 'rerecord_base') && !isGNSSMode

  const { facilityGeoPoint, contextPins, robots: facilityContextRobots, loading: facilityGeoLoading } =
    useFacilityDialogGeoContext(robotInfo.facilityId, Boolean(isModalOpen && showFacilityOriginMap))

  const hasValidTelemetryCoordinates = useMemo(
    () => hasValidNonZeroGeoPosition(robotInfo.lastPosition),
    [robotInfo.lastPosition]
  )

  const isLocationNameFilled = useMemo(() => !!formData.locationName?.trim(), [formData.locationName])
  const isDuplicateName = useMemo(() => {
    if (!formData.locationName?.trim()) return false
    let fullName = formData.locationName.trim()
    return availableLocations.includes(fullName)
  }, [formData.locationName, formData.subMode, robotInfo.facilityId, availableLocations])

  const isReservedName = useMemo(() => {
    const name = formData.locationName
      ?.trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '')
    if (!name) return false
    const cleanInputName = name.replace(/v\d+$/, '')

    const baseMapName = t('robot.base_map')
      .toLowerCase()
      .replace(/[\s_]+/g, '')
    const cleanBaseMapName = baseMapName.replace(/v\d+$/, '')

    return (
      name === 'basemap' || cleanInputName === 'basemap' || name === baseMapName || cleanInputName === cleanBaseMapName
    )
  }, [formData.locationName, t])

  const positionCoordinatesOk = !isGNSSMode || hasValidTelemetryCoordinates

  useEffect(() => {
    if (!isModalOpen) {
      setShowNewFacilityWarning(false)
      setShowForceBaseWarning(false)
    }
  }, [isModalOpen])

  useEffect(() => {
    if (isGNSSMode && formData.subMode === 'update_existing') {
      onFormDataChange({
        subMode: 'new_location',
        locationName: undefined,
        selectedLocationName: undefined,
        robotPosition: undefined,
        robotYaw: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGNSSMode, formData.subMode])

  useEffect(() => {
    const baseOrigin = formData.subMode === 'new_facility' || formData.subMode === 'rerecord_base'
    if (baseOrigin && previousSubModeRef.current === formData.subMode && !isGNSSMode) {
      onFormDataChange({
        ...formData,
        robotPosition,
        robotYaw,
      })
    }
    previousSubModeRef.current = formData.subMode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotPosition, robotYaw, formData.subMode, isGNSSMode, onFormDataChange])

  useEffect(() => {
    let isValid = false
    switch (formData.subMode) {
      case 'update_existing':
        isValid = !!formData.selectedLocationName && positionCoordinatesOk
        break
      case 'new_location':
        isValid =
          isLocationNameFilled && positionCoordinatesOk && !isDuplicateName && !isReservedName && !!robotInfo.facilityId
        break
      case 'new_facility':
      case 'rerecord_base': {
        const autoName = t('robot.base_map')
        const isAutoNameValid = !!autoName
        if (isGNSSMode) {
          isValid = isAutoNameValid && positionCoordinatesOk
        } else {
          const hasPosition = !!robotPosition
          const hasYaw = robotYaw !== undefined
          isValid = isAutoNameValid && hasPosition && hasYaw
        }
        break
      }
    }
    onValidationChange(isValid)
  }, [
    formData.selectedLocationName,
    formData.subMode,
    robotPosition,
    robotYaw,
    isGNSSMode,
    positionCoordinatesOk,
    onValidationChange,
    isLocationNameFilled,
    isDuplicateName,
    isReservedName,
    robotInfo.facilityId,
  ])

  const onMapInited = useCallback(() => {
    setMapInited(true)
  }, [])

  const handlePositionChanged = useCallback((point: Point) => {
    setRobotPosition(point)
    setRobotYaw(prev => (prev === undefined ? 0 : prev))
  }, [])

  const handleYawChanged = useCallback((yaw: number) => {
    setRobotYaw(yaw)
  }, [])

  const isBaseOriginMode = (mode: MappingSubMode) => mode === 'new_facility' || mode === 'rerecord_base'

  const applySubModeChange = useCallback(
    (mode: MappingSubMode) => {
      if (isGNSSMode && mode === 'update_existing') {
        return
      }

      previousSubModeRef.current = mode

      const shouldPreserveLocationName =
        (formData.subMode === 'new_location' || formData.subMode === 'new_facility') &&
        (mode === 'new_location' || mode === 'new_facility')

      onFormDataChange({
        subMode: mode,
        locationName: shouldPreserveLocationName ? formData.locationName : undefined,
        selectedLocationName: mode === 'update_existing' ? currentMapName : undefined,
        robotPosition: isBaseOriginMode(mode) && !isGNSSMode ? robotPosition : undefined,
        robotYaw: isBaseOriginMode(mode) && !isGNSSMode ? robotYaw : undefined,
      })
    },
    [isGNSSMode, formData.subMode, formData.locationName, currentMapName, robotPosition, robotYaw, onFormDataChange]
  )

  const handleSubModeChange = useCallback(
    (mode: MappingSubMode) => {
      if (isGNSSMode && mode === 'update_existing') {
        return
      }

      if (mode === 'new_facility' && formData.subMode !== 'new_facility') {
        setShowNewFacilityWarning(true)
        return
      }

      if (mode === 'rerecord_base' && formData.subMode !== 'rerecord_base') {
        setShowForceBaseWarning(true)
        return
      }

      applySubModeChange(mode)
    },
    [isGNSSMode, formData.subMode, applySubModeChange]
  )

  const handleConfirmNewFacility = useCallback(() => {
    setShowNewFacilityWarning(false)
    applySubModeChange('new_facility')
  }, [applySubModeChange])

  const handleCloseNewFacilityWarning = useCallback(() => {
    setShowNewFacilityWarning(false)
    applySubModeChange('new_location')
  }, [applySubModeChange])

  const handleConfirmForceBase = useCallback(() => {
    setShowForceBaseWarning(false)
    applySubModeChange('rerecord_base')
  }, [applySubModeChange])

  const handleCloseForceBaseWarning = useCallback(() => {
    setShowForceBaseWarning(false)
  }, [])

  const handleLocationNameChange = useCallback(
    (value: string) => {
      const sanitizedValue = value.replace(LOCATION_NAME_SANITIZE_REGEX, '').replace(/\s+/g, ' ').replace(/^\s+/, '')
      onFormDataChange({
        ...formData,
        locationName: sanitizedValue,
      })
    },
    [formData, onFormDataChange]
  )

  const needsPlacement = showFacilityOriginMap && (!robotPosition || robotYaw === undefined)

  const showTextField = formData.subMode === 'new_location'
  const showMap = showFacilityOriginMap
  const showPositionInfo = showFacilityOriginMap
  const showHelperText = showTextField
  const showForceBaseRecord = hasBaseMap && allowForceBaseRecord

  const facilityCameraKey = [
    robotInfo.facilityId ?? 'nofac',
    facilityGeoPoint?.coordinates?.join(',') ?? 'nogeo',
    contextPins.map(pin => pin.id).join('|') || 'nopins',
  ].join(':')

  useEffect(() => {
    if (!showMap || !mapInited || facilityGeoLoading) {
      return
    }
    const map = mapRef.current?.getMap()
    if (!map) {
      return
    }
    if (facilityCameraAppliedKeyRef.current === facilityCameraKey) {
      return
    }
    facilityCameraAppliedKeyRef.current = facilityCameraKey
    cancelFacilityCameraRef.current?.()
    cancelFacilityCameraRef.current = applyAssetDialogMapCamera(
      map,
      'create',
      null,
      facilityGeoPoint,
      contextPins.map(pin => pin.point)
    )
    return () => {
      cancelFacilityCameraRef.current?.()
      cancelFacilityCameraRef.current = null
    }
  }, [showMap, mapInited, facilityGeoLoading, facilityCameraKey, facilityGeoPoint, contextPins])

  useEffect(() => {
    if (!showMap) {
      facilityCameraAppliedKeyRef.current = null
      cancelFacilityCameraRef.current?.()
      cancelFacilityCameraRef.current = null
      setMapInited(false)
    }
  }, [showMap])

  const infoMessages = useMemo(() => {
    const items: { kind: 'info' | 'warning'; text: string }[] = []

    if (formData.subMode === 'new_location') {
      items.push({ kind: 'info', text: t('robot.map_recording_new_location_from_current') })
    }
    if (formData.subMode === 'new_facility' || formData.subMode === 'rerecord_base') {
      items.push({ kind: 'warning', text: t('robot.map_recording_new_facility_stations') })
    }
    if (isGNSSMode && hasValidTelemetryCoordinates) {
      items.push({ kind: 'info', text: t('robot.map_recording_gnss_space_hint') })
    }
    const showMapForFacility = isBaseOriginMode(formData.subMode) && !isGNSSMode
    if (isLidarMode && showMapForFacility) {
      items.push({ kind: 'info', text: t('robot.map_recording_lidar_space_hint') })
    }
    return items
  }, [formData.subMode, isGNSSMode, isLidarMode, hasValidTelemetryCoordinates, t])

  const showAnyError = needsPlacement || (isGNSSMode && !hasValidTelemetryCoordinates)
  const showAlertsBlock = showAnyError || infoMessages.length > 0

  return (
    <div className={clsx(style.contentWrapper, { [style.disabled]: isDisabled })}>
      <ConfirmDialog
        title={t('robot.map_recording_new_facility_warning_title')}
        isOpen={showNewFacilityWarning}
        onClose={handleCloseNewFacilityWarning}
        onConfirmClick={handleConfirmNewFacility}
        confirmText={t('robot.map_recording_confirm')}
        danger={true}
        overlayZIndex={110}
      >
        <div className={style.confirmDialogBody}>{t('robot.map_recording_new_facility_warning_body')}</div>
      </ConfirmDialog>
      <ConfirmDialog
        title={t('robot.map_recording_force_base_warning_title')}
        isOpen={showForceBaseWarning}
        onClose={handleCloseForceBaseWarning}
        onConfirmClick={handleConfirmForceBase}
        confirmText={t('robot.map_recording_confirm')}
        danger={true}
        overlayZIndex={110}
      >
        <div className={style.confirmDialogBody}>
          <p>{t('robot.map_recording_force_base_warning_standard')}</p>
          <p>{t('robot.map_recording_force_base_warning_impact')}</p>
          <p>{t('robot.map_recording_force_base_warning_session')}</p>
        </div>
      </ConfirmDialog>

      <div className={style.robotInfo}>
        <div className={style.robotInfoRow}>
          <span className={style.robotInfoLabel}>{t('robot.map_recording_serial')}:</span>
          <span className={style.robotInfoValue}>{robotInfo.serialNumber}</span>
        </div>
        <div className={style.robotInfoRow}>
          <span className={style.robotInfoLabel}>{t('robot.map_recording_name')}:</span>
          <span className={style.robotInfoValue}>{robotInfo.name || t('robot.map_recording_na')}</span>
        </div>
        {getRobotTypeDisplayName(robotInfo.vendor, robotInfo.robot_type) && (
          <div className={style.robotInfoRow}>
            <span className={style.robotInfoLabel}>{t('robot.map_recording_type')}:</span>
            <span className={style.robotInfoValue}>
              {getRobotTypeDisplayName(robotInfo.vendor, robotInfo.robot_type)}
            </span>
          </div>
        )}
        <div className={style.robotInfoRow}>
          <span className={style.robotInfoLabel}>{t('robot.current_facility', 'Facility')}:</span>
          <span className={style.robotInfoValue}>{robotInfo.facilityName || '—'}</span>
        </div>
        <div className={clsx(style.robotInfoRow, { [style.hidden]: !showPositionInfo })}>
          <span className={style.robotInfoLabel}>{t('robot.map_recording_position')}:</span>
          <span className={style.robotInfoValue}>
            {robotPosition
              ? `${robotPosition.coordinates[0].toFixed(6)}, ${robotPosition.coordinates[1].toFixed(6)}`
              : t('robot.map_recording_not_set')}
          </span>
        </div>
        <div className={clsx(style.robotInfoRow, { [style.hidden]: !showPositionInfo })}>
          <span className={style.robotInfoLabel}>{t('robot.map_recording_orientation')}:</span>
          <span className={style.robotInfoValue}>
            {robotPosition && robotYaw !== undefined
              ? `${convertYawUiToUserDegrees(robotYaw).toFixed(1)}°`
              : t('robot.map_recording_not_set')}
          </span>
        </div>
      </div>

      <div className={style.modeSelection}>
        <div className={style.modeSelectionTitle}>{t('robot.map_recording_select_mode')}</div>
        <div className={style.radioButtons}>
          <div className={style.radioButton}>
            <RadioButton
              label={t('robot.map_recording_mode_new_location')}
              checked={formData.subMode === 'new_location'}
              onChange={() => handleSubModeChange('new_location')}
              disabled={isDisabled || !hasBaseMap}
              title={
                !hasBaseMap
                  ? t(
                      'robot.map_recording_no_base_map_error',
                      'This robot needs a base map on this facility before adding locations'
                    )
                  : undefined
              }
            />
          </div>
          <div className={style.radioButton}>
            <RadioButton
              label={
                currentMapName
                  ? `${t('robot.map_recording_mode_update_current')} ("${currentMapName}")`
                  : t('robot.map_recording_mode_update_current')
              }
              checked={formData.subMode === 'update_existing'}
              onChange={() => handleSubModeChange('update_existing')}
              disabled={isDisabled || !hasBaseMap}
              title={
                !hasBaseMap
                  ? t(
                      'robot.map_recording_no_base_map_error',
                      'This robot needs a base map on this facility before adding locations'
                    )
                  : undefined
              }
            />
          </div>
          <div className={style.radioButton}>
            <RadioButton
              label={t('robot.map_recording_mode_new_facility')}
              checked={formData.subMode === 'new_facility'}
              onChange={() => handleSubModeChange('new_facility')}
              disabled={isDisabled || !robotInfo.facilityId || hasBaseMap}
              title={
                !robotInfo.facilityId
                  ? t(
                      'robot.map_recording_attached_to_facility_error',
                      'The robot must be attached to a facility to start deployment'
                    )
                  : hasBaseMap
                  ? t('robot.map_recording_base_map_exists_error', 'This robot already has a base map on this facility')
                  : undefined
              }
            />
          </div>
          {showForceBaseRecord && (
            <div className={style.radioButton}>
              <RadioButton
                label={t('robot.map_recording_mode_force_base')}
                checked={formData.subMode === 'rerecord_base'}
                onChange={() => handleSubModeChange('rerecord_base')}
                disabled={isDisabled || !robotInfo.facilityId}
                title={t('robot.map_recording_force_base_hint')}
              />
            </div>
          )}
        </div>
      </div>

      <div className={style.locationField}>
        <div className={clsx(style.locationFieldContent, { [style.hidden]: !showTextField })}>
          <TextFieldDescribed
            description={t('robot.map_recording_location_name')}
            value={formData.locationName || ''}
            onChange={e => handleLocationNameChange(e.target.value)}
            placeholder={t('robot.map_recording_enter_location_placeholder')}
            className={style.textfield}
            disabled={isDisabled}
            hasError={
              (!!formData.locationName && (isDuplicateName || isReservedName)) ||
              (formData.subMode === 'new_location' && !isLocationNameFilled)
            }
            error={
              isReservedName
                ? t('robot.map_recording_location_reserved', `Name "${t('robot.base_map')}" is reserved`)
                : isDuplicateName
                ? t('robot.map_recording_location_duplicate', 'Map name already exists')
                : !isLocationNameFilled
                ? t('robot.map_recording_location_required')
                : undefined
            }
          />
        </div>
        {isBaseOriginMode(formData.subMode) && (
          <div className={style.locationFieldContent}>
            <div className={style.fieldLabel}>{t('robot.map_recording_location_label')}:</div>
            <div className={style.autoNameValue}>{t('robot.base_map')}</div>
          </div>
        )}
      </div>

      <div className={clsx(style.helperTextContainer, { [style.hidden]: !showHelperText })}>
        <div
          className={clsx(style.helperText, {
            [style.helperTextError]: !isLocationNameFilled,
          })}
        >
          {t('robot.map_recording_location_name_hint')}
        </div>
      </div>

      <div className={clsx(showMap ? style.map : style.mapHidden)}>
        {showMap && (
          <MapboxCommon disableRuler={true} ref={mapRef} onMapInited={onMapInited}>
            {mapInited && (
              <>
                <FacilityDialogGeoOverlay
                  map={mapRef.current?.getMap()}
                  contextPins={contextPins}
                  robots={facilityContextRobots}
                />
                <RobotOnMapEditable
                  map={mapRef.current?.getMap()}
                  position={robotPosition}
                  yaw={robotYaw}
                  onPositionChanged={handlePositionChanged}
                  onYawChanged={handleYawChanged}
                />
              </>
            )}
          </MapboxCommon>
        )}
      </div>

      <div className={clsx(style.alertsContainer, { [style.hidden]: !showAlertsBlock })}>
        {needsPlacement && (
          <div className={clsx(style.alert, style.alertError)}>{t('robot.map_recording_placement_error')}</div>
        )}
        {isGNSSMode && !hasValidTelemetryCoordinates && (
          <div className={clsx(style.alert, style.alertError)}>{t('robot.map_recording_gnss_coords_invalid')}</div>
        )}
        {infoMessages.map((msg, idx) => (
          <div
            key={`${msg.kind}-${idx}`}
            className={clsx(style.alert, msg.kind === 'warning' ? style.alertWarning : style.alertInfo)}
          >
            {msg.text}
          </div>
        ))}
      </div>
    </div>
  )
}

export default MapRecordingView
